// Module assembly — pure merge/placement of module picks into a flat block
// list. Shared by Studio insert (merge rule), Add to Project, and Export from
// Masters. No React, no ProseMirror. See docs/MODULE_ASSEMBLY_PLAN.md §1.

import type { ContentBlock, StandardConfig } from './types';
import type { NumberedRef } from './body-layout';
import { computeNumbering } from './body-layout';
import { createGroupRemap } from './block-groups';
import { normalizeHeading } from './heading-normalize';
import type { ModuleChunk, ModuleStamp, SpecModule } from './spec-modules';
import {
  endOfSubtree,
  listModuleInsertions,
  matchAnchor,
  newInsertId,
  remapInsertGroups,
  stampChunkForInsert,
  stampOf,
} from './spec-modules';

/** One spec's module picks from the Modules stepper dialog. */
export interface ModulePicks {
  /** Library modules to insert that are not yet in the spec. */
  include: SpecModule[];
  /** Every `insertId` of instances the user unchecked (all instances of a
   *  module when it appears more than once). */
  excludeInsertIds: string[];
}

export const EMPTY_PICKS: ModulePicks = { include: [], excludeInsertIds: [] };

const levelOf = (b: ContentBlock): number => Math.max(1, Math.min(9, b.level || 1));

export { normalizeHeading };

/** Where a chunk merges: the matched article, and the block to insert after
 *  (the end of that article's subtree). `label` names it for copy. */
export interface HeadingMergeTarget {
  articleBlockId: string;
  insertAfterBlockId: string;
  /** "3.2 INSTALLATION" */
  label: string;
}

/** Index range [start, end) of Part `partIndex` (1-based) among numbered
 *  level-1 blocks, or null when the spec has no such Part. */
function partRange(
  blocks: ContentBlock[],
  refs: NumberedRef[],
  partIndex: number
): [number, number] | null {
  if (!(partIndex > 0)) return null;
  const refById = new Map(refs.map((r) => [r.id, r]));
  const starts: number[] = [];
  blocks.forEach((b, i) => {
    if (levelOf(b) === 1 && refById.get(b.id)?.prefix) starts.push(i);
  });
  const start = starts[partIndex - 1];
  if (start === undefined) return null;
  return [start, starts[partIndex] ?? blocks.length];
}

/**
 * Heading-merge rule: does the chunk's FIRST block (a level-2 article
 * heading) already exist as a level-2 block inside Part `partIndex`? Exact
 * match after `normalizeHeading`, never across Parts. Null = no merge.
 */
export function findHeadingMergeTarget(
  blocks: ContentBlock[],
  chunk: Pick<ModuleChunk, 'blocks'>,
  partIndex: number,
  refs: NumberedRef[]
): HeadingMergeTarget | null {
  const first = chunk?.blocks?.[0];
  if (!first || levelOf(first as ContentBlock) !== 2) return null;
  const want = normalizeHeading(first.text || '');
  if (!want) return null;
  const range = partRange(blocks, refs, partIndex);
  if (!range) return null;
  const refById = new Map(refs.map((r) => [r.id, r]));
  for (let i = range[0] + 1; i < range[1]; i++) {
    const b = blocks[i];
    const ref = refById.get(b.id);
    if (levelOf(b) !== 2 || !ref?.prefix) continue;
    if (normalizeHeading(b.text || '') !== want) continue;
    const end = endOfSubtree(i, 2, blocks, refs);
    return {
      articleBlockId: b.id,
      insertAfterBlockId: blocks[end].id,
      label: `${ref.prefix} ${(b.text || '').trim()}`.trim(),
    };
  }
  return null;
}

/** Last block of Part `partIndex`, or the last block of the spec. */
function endOfPartId(
  blocks: ContentBlock[],
  refs: NumberedRef[],
  partIndex: number
): string | null {
  if (!blocks.length) return null;
  const range = partRange(blocks, refs, partIndex);
  return blocks[(range ? range[1] : blocks.length) - 1].id;
}

/** Pure composition: drop excluded instances, stamp + place included
 *  modules by the heading-merge rule. Stamps are kept; the caller
 *  rasterizes (strips `moduleStamp`) when the result leaves the masters. */
export function assembleSpec(
  blocks: ContentBlock[],
  picks: ModulePicks,
  numbering: StandardConfig | null | undefined
): ContentBlock[] {
  const exclude = new Set(picks?.excludeInsertIds || []);
  let list = (blocks || []).filter((b) => {
    const stamp = stampOf(b);
    return !(stamp && exclude.has(stamp.insertId));
  });

  for (const module of picks?.include || []) {
    if (listModuleInsertions(list).some((i) => i.moduleId === module.id)) continue;
    const stamp: ModuleStamp = {
      moduleId: module.id,
      moduleName: module.name,
      moduleVersion: module.version ?? 1,
      insertId: newInsertId(),
    };
    const remap = createGroupRemap(() => `grp-${Math.random().toString(36).slice(2, 10)}`);
    for (const chunk of module.chunks || []) {
      const refs = computeNumbering(list, numbering);
      const part = chunk.anchor?.partIndex ?? 0;
      const merge = findHeadingMergeTarget(list, chunk, part, refs);
      let stamped = remapInsertGroups(
        stampChunkForInsert(chunk, merge ? { ...stamp, mergedHeading: true } : stamp),
        remap
      );
      if (merge) stamped = stamped.slice(1);
      if (!stamped.length) continue;

      let afterId: string | null;
      if (merge) afterId = merge.insertAfterBlockId;
      else {
        const match = matchAnchor(chunk.anchor, list, refs);
        afterId =
          match.kind === 'unique' ? match.insertAfterBlockId : endOfPartId(list, refs, part);
      }
      const at = afterId ? list.findIndex((b) => b.id === afterId) + 1 : list.length;
      list = [...list.slice(0, at), ...stamped, ...list.slice(at)];
    }
  }
  return list;
}
